import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ChevronDown, FileText, FileDown, BookOpen } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LessonSummaryMenu — the per-stage three-option affordance that lives
 * inside a Session-notes card. Replaces the old "Download .md" button
 * with a quiet chevron trigger → stacked menu with View / Download PDF /
 * Download Markdown.
 *
 * The parent passes `topicId`, `stageIdx`, and `stageTitle`. All three
 * actions call the same backend endpoints — the only difference is the
 * client-side handling (modal vs blob download).
 *
 * Legacy notes without ## sections are silently regenerated by the
 * backend on first access. The parent shows an "updating notes…"
 * indicator while any action is in flight so the learner sees a brief,
 * unobtrusive cue rather than a modal interruption.
 */
export default function LessonSummaryMenu({
  topicId,
  stageIdx,
  stageTitle,
  authHeaders,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewPayload, setViewPayload] = useState(null); // open modal when truthy
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close menu on outside click / Esc.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const headers = () => authHeaders?.() || {};

  // --- Action: View ---
  const openView = async () => {
    setMenuOpen(false);
    setBusy(true);
    try {
      const res = await axios.get(
        `${API}/learn/topics/${topicId}/stages/${stageIdx}/summary`,
        { headers: headers() },
      );
      setViewPayload(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open summary');
    } finally {
      setBusy(false);
    }
  };

  // --- Action: Download (shared by PDF + MD) ---
  const downloadAs = async (fmt) => {
    setMenuOpen(false);
    setBusy(true);
    try {
      const res = await axios.get(
        `${API}/learn/topics/${topicId}/stages/${stageIdx}/summary/download`,
        {
          headers: headers(),
          params: { format: fmt },
          responseType: 'blob',
        },
      );
      // Pull filename from Content-Disposition when the server sets it;
      // fallback to a reasonable client-side name so download never fails.
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = (match && match[1]) ||
        `sage-${stageTitle.replace(/\W+/g, '-').toLowerCase()}.${fmt}`;
      const blob = new Blob(
        [res.data],
        { type: fmt === 'pdf' ? 'application/pdf' : 'text/markdown' },
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          disabled={busy}
          data-testid={`lesson-summary-trigger-${stageIdx}`}
          className="text-xs text-zinc-300 hover:text-white flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>updating notes…</span>
            </>
          ) : (
            <>
              <span>Lesson summary</span>
              <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>

        {menuOpen && (
          <div
            ref={menuRef}
            className="absolute z-20 mt-1 left-0 w-44 bg-stone-950/95 backdrop-blur-sm border border-white/10 rounded-sm shadow-2xl py-1"
            data-testid={`lesson-summary-menu-${stageIdx}`}
          >
            <MenuItem
              icon={<BookOpen className="w-3.5 h-3.5" />}
              label="View"
              onClick={openView}
              testid={`lesson-summary-view-${stageIdx}`}
            />
            <MenuItem
              icon={<FileDown className="w-3.5 h-3.5" />}
              label="Download PDF"
              onClick={() => downloadAs('pdf')}
              testid={`lesson-summary-pdf-${stageIdx}`}
            />
            <MenuItem
              icon={<FileText className="w-3.5 h-3.5" />}
              label="Download Markdown"
              onClick={() => downloadAs('md')}
              testid={`lesson-summary-md-${stageIdx}`}
            />
          </div>
        )}
      </div>

      {viewPayload && (
        <LessonSummaryModal
          payload={viewPayload}
          onClose={() => setViewPayload(null)}
        />
      )}
    </>
  );
}

const MenuItem = ({ icon, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full text-left px-3 py-2 text-xs text-zinc-200 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors"
  >
    <span className="text-zinc-400">{icon}</span>
    <span>{label}</span>
  </button>
);

/**
 * In-app reader for a stage summary. Dark card on dim backdrop in dark
 * theme (linen variant inherits from the global theme — the backdrop
 * tint comes from `.summary-modal-backdrop`). Only the modal scrolls;
 * the underlying page sits still.
 */
function LessonSummaryModal({ payload, onClose }) {
  const scrollerRef = useRef(null);
  const [flashedSlug, setFlashedSlug] = useState(null);

  // Lock the underlying page scroll while the modal is open — the PRD
  // wants only the modal to scroll, so stash and restore document body
  // overflow around the modal's lifetime.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const jumpTo = (slug) => {
    const el = scrollerRef.current?.querySelector(`[data-section-slug="${slug}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Brief highlight so the eye lands on the destination heading.
    setFlashedSlug(slug);
    setTimeout(() => setFlashedSlug(null), 1200);
  };

  const createdLine = payload.created_at
    ? new Date(payload.created_at).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-10"
      data-testid="lesson-summary-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl max-h-full bg-stone-950/95 border border-white/10 rounded-sm shadow-2xl flex flex-col"
      >
        {/* Header strip */}
        <div className="flex items-start justify-between px-8 pt-8 pb-4 border-b border-white/5">
          <div className="min-w-0 pr-4">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-1 truncate">
              {payload.topic_title}
            </p>
            <h2
              className="text-3xl font-light text-white leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {payload.stage_name}
            </h2>
            {createdLine && (
              <p className="text-xs text-zinc-500 mt-1">{createdLine}</p>
            )}
          </div>
          <button
            onClick={onClose}
            type="button"
            data-testid="lesson-summary-close"
            className="text-zinc-400 hover:text-white shrink-0 p-1"
            aria-label="Close summary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scroll area */}
        <div
          ref={scrollerRef}
          className="overflow-y-auto px-8 py-6 flex-1"
        >
          {payload.has_index && (
            <section className="mb-8">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">
                Contents
              </p>
              <ol className="space-y-1.5 list-none p-0">
                {payload.sections.map((s, i) => (
                  <li key={s.slug}>
                    <button
                      type="button"
                      onClick={() => jumpTo(s.slug)}
                      data-testid={`lesson-summary-jump-${i}`}
                      className="text-sm text-zinc-300 hover:text-white text-left"
                    >
                      <span className="text-zinc-500 mr-2">{i + 1}.</span>
                      {s.heading}
                    </button>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {payload.sections.map((s) => (
            <section key={s.slug} className="mb-8 last:mb-0">
              <h3
                data-section-slug={s.slug}
                className={`text-xl text-white mb-3 transition-colors duration-500 ${
                  flashedSlug === s.slug ? 'text-amber-200' : ''
                }`}
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {s.heading}
              </h3>
              <div className="space-y-3 text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                {s.body.split(/\n{2,}/).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer: optional close button for keyboard-less touch exits */}
        <div className="px-8 py-4 border-t border-white/5 flex justify-end">
          <Button
            onClick={onClose}
            variant="ghost"
            data-testid="lesson-summary-done"
            className="text-zinc-400 hover:text-white"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
